import Snackbar from "@mui/material/Snackbar";
import MuiAlert, { AlertProps, AlertColor } from "@mui/material/Alert";
import { Fragment, forwardRef } from "react";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import REFRESH from "@/icons/Refresh";
import { extractDomain } from "@/utils/index";

export type ToastMessageProps = {
  showToastMessage: boolean;
  message: any;
  toastSeverity: AlertColor;
  handleToastClose: VoidFunction;
  shouldAutoHide?: boolean;
  isReload?: boolean;
  isDownload?: boolean;
  download?: (data: any) => void;
  data?: any;
  emailCTA?: boolean;
  duration?: number;
  isRedirect?: boolean;
};

const Alert = forwardRef<HTMLDivElement, AlertProps>(function Alert(
  props,
  ref
) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

function formatTextWithLineBreaksAndLinks(text: any) {
  const t = typeof text === "string" ? text : text?.length ? text[0] : "";
  const lines = t?.split("\n");
  return (
    <div>
      {lines.map((line: any, index: any) => {
        const modifiedLine = line.replace(
          /Access & Settings/g,
          `<a href="${extractDomain()}/edge/user-management?tab=contactInfo" target="_blank" rel="noopener noreferrer" style="color: white; font-weight: 500;">Access & Settings</a>`
        );

        return (
          <Fragment key={index}>
            <span
              dangerouslySetInnerHTML={{
                __html: modifiedLine,
              }}
            ></span>
            {index < lines.length - 1 && <br />}
          </Fragment>
        );
      })}
    </div>
  );
}

const ToastMessage = (props: ToastMessageProps) => {
  const {
    showToastMessage,
    message,
    toastSeverity,
    shouldAutoHide = true,
    handleToastClose,
    isReload,
    isDownload,
    download = () => {},
    data,
    emailCTA,
    duration = 5000,
    isRedirect = false,
  } = props;

  const handleClose = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }

    handleToastClose();
  };

  let customIcon = undefined;
  let bgColor = "";

  if (toastSeverity === "success") {
    customIcon = <CheckCircleOutlineIcon fontSize="inherit" />;
    bgColor = "var(--clr-state-success-green)";
  }
  if (toastSeverity === "info") {
    customIcon = <InfoOutlinedIcon fontSize="inherit" />;
    bgColor = "var(--clr-grey-1)";
  }
  if (toastSeverity === "warning") {
    customIcon = <ErrorOutlineIcon fontSize="inherit" />;
    bgColor = "var(--clr-state-warning-yellow)";
  }
  if (toastSeverity === "error") {
    customIcon = <WarningAmberIcon fontSize="inherit" />;
    bgColor = "var(--clr-state-failure-red)";
  }

  return (
    <Snackbar
      open={showToastMessage}
      autoHideDuration={shouldAutoHide ? duration : null}
      onClose={handleClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
    >
      <Alert
        onClose={handleClose}
        severity={toastSeverity}
        icon={customIcon}
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: "8px",
          minWidth: "360px",
          borderRadius: "8px",
          fontSize: "14px",
          fontWeight: "400",
          backgroundColor: bgColor,
        }}
      >
        {isRedirect ? (
          <div dangerouslySetInnerHTML={{ __html: message }}></div>
        ) : (
          formatTextWithLineBreaksAndLinks(message)
        )}
        {!!emailCTA && (
          <>
            <br />
            <a href="mailto:cr@foundit.in">Contact Customer Support</a>
          </>
        )}

        {isReload && !isDownload && (
          <span
            style={{ cursor: "pointer", position: "relative", top: "0.4rem" }}
            onClick={(event) => {
              event.preventDefault();
              window.location.reload();
            }}
          >
            {REFRESH}
          </span>
        )}

        {isDownload && (
          <>
            <br />
            <span
              style={{ cursor: "pointer", textDecoration: "underline" }}
              onClick={(event) => {
                event.preventDefault();
                download(data);
              }}
            >
              Open
            </span>
          </>
        )}
      </Alert>
    </Snackbar>
  );
};

export default ToastMessage;
